/**
 * Token persistence. Keys + semantics kept compatible with the legacy @console
 * app so an existing session in the same browser keeps working:
 *   SA  = access token
 *   SR  = refresh token
 *   EXP = access-token expiry as an absolute UNIX timestamp in **seconds**
 *   UUID = user id
 */
const KEY = {
  access: 'SA',
  refresh: 'SR',
  expiry: 'EXP',
  uuid: 'UUID',
  authMethod: 'AUTH_METHOD',
  // Impersonation session marker — set only when a DC employee impersonates this
  // customer contact (via /auth/:method/impersonate). Absent for a direct login.
  // imp_by_* is the non-authoritative display fallback; impersonationToken is the
  // encrypted token verified server-side by @mgmt (the trusted source).
  impersonatorId: 'IMP_BY_ID',
  impersonatorName: 'IMP_BY_NAME',
  impersonatorEmail: 'IMP_BY_EMAIL',
  impersonationToken: 'IMP_TOKEN',
} as const;

export type AuthMethod = 'local' | 'sso' | 'google';

/** DC employee behind an impersonation session (see ImpersonationPage). */
export interface Impersonator {
  id: string;
  name: string;
  email: string;
}

export interface SetTokenInput {
  access: string;
  refresh?: string;
  /** Absolute expiry as a UNIX timestamp in seconds (the API's `exp`). */
  expSeconds?: number;
  uuid?: string;
  authMethod?: AuthMethod;
  /** Present only for a DC-driven impersonation session. */
  impersonatedBy?: Impersonator;
  /** Encrypted impersonation token, verified server-side by @mgmt. */
  impersonationToken?: string;
}

export const tokenStorage = {
  get access() {
    return localStorage.getItem(KEY.access);
  },
  get refresh() {
    return localStorage.getItem(KEY.refresh);
  },
  /** Expiry as absolute UNIX seconds, or null if unknown. */
  get expSeconds(): number | null {
    const v = localStorage.getItem(KEY.expiry);
    return v ? Number(v) : null;
  },
  get uuid() {
    return localStorage.getItem(KEY.uuid);
  },
  get authMethod(): AuthMethod {
    // 'sso' and 'google' are both Keycloak-issued → refresh against Keycloak;
    // only classic 'local' refreshes against the local auth service. See
    // refreshAccessToken in api/client.ts.
    const v = localStorage.getItem(KEY.authMethod);
    return v === 'sso' || v === 'google' ? v : 'local';
  },
  get isExpired(): boolean {
    const exp = this.expSeconds;
    if (!exp) return true;
    return Date.now() / 1000 >= exp;
  },
  /** True when this session was created by a DC employee impersonating the contact. */
  get isImpersonating(): boolean {
    return !!localStorage.getItem(KEY.impersonatorName) || !!localStorage.getItem(KEY.impersonatorId);
  },
  /** The DC employee behind the impersonation, or null for a direct login. */
  get impersonatedBy(): Impersonator | null {
    if (!this.isImpersonating) return null;
    return {
      id: localStorage.getItem(KEY.impersonatorId) ?? '',
      name: localStorage.getItem(KEY.impersonatorName) ?? '',
      email: localStorage.getItem(KEY.impersonatorEmail) ?? '',
    };
  },
  /** The encrypted impersonation token to verify with @mgmt, or null. */
  get impersonationToken(): string | null {
    return localStorage.getItem(KEY.impersonationToken);
  },
  set({ access, refresh, expSeconds, uuid, authMethod, impersonatedBy, impersonationToken }: SetTokenInput) {
    localStorage.setItem(KEY.access, access);
    if (refresh) localStorage.setItem(KEY.refresh, refresh);
    if (expSeconds != null) localStorage.setItem(KEY.expiry, String(expSeconds));
    if (uuid) localStorage.setItem(KEY.uuid, uuid);
    if (authMethod) localStorage.setItem(KEY.authMethod, authMethod);
    if (impersonatedBy) {
      if (impersonatedBy.id) localStorage.setItem(KEY.impersonatorId, impersonatedBy.id);
      if (impersonatedBy.name) localStorage.setItem(KEY.impersonatorName, impersonatedBy.name);
      if (impersonatedBy.email) localStorage.setItem(KEY.impersonatorEmail, impersonatedBy.email);
    }
    if (impersonationToken) localStorage.setItem(KEY.impersonationToken, impersonationToken);
  },
  clear() {
    Object.values(KEY).forEach((k) => localStorage.removeItem(k));
    // Drop any in-flight SSO handshake state so an aborted login or a logout
    // doesn't leak the PKCE verifier / intended URL into the next session.
    // Keys hardcoded to avoid a circular import with `lib/sso`.
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('intended_url');
  },
};
